/* global Word, performance, console, __MSS_DEBUG_LOGGING__ */

/**
 * Run-scoped performance instrumentation for the styling pipeline.
 *
 * Office.js queues operations client-side and only talks to Word on
 * `context.sync()`, so everything between syncs is essentially free JS.
 * Per-sync attribution is therefore the only measurement that localises cost
 * — hence `timedSync` rather than coarse stage timers.
 *
 * The last completed report is retained in-module so the taskpane can render
 * it as copyable text; users can't share a browser console.
 */

export type PerfMeta = Record<string, number | string | boolean>;

export interface PerfEntry {
  name: string;
  durationMs: number;
  /** Offset from run start, for spotting gaps between entries. */
  atMs: number;
  meta?: PerfMeta;
}

export interface PerfReport {
  label: string;
  totalMs: number;
  /** Wall-clock not covered by any entry — a large value means a sync is unmeasured. */
  unattributedMs: number;
  /**
   * Epoch ms at which the run started.
   *
   * Every `atMs` is relative to the session, which is enough to read a report on
   * its own but not to place it on a wall clock. Sentry spans need absolute
   * timestamps, so the anchor has to leave the session with the report.
   */
  startedAtEpochMs: number;
  entries: PerfEntry[];
}

let lastReport: PerfReport | null = null;

export function getLastPerfReport(): PerfReport | null {
  return lastReport;
}

/**
 * Console output is gated on `MSS_DEBUG_LOGGING`; the timings themselves are
 * always collected, so an enabled and a disabled build take an identical code
 * path through the apply stages. Only what reaches the console differs.
 *
 * A run emits ~70 of these lines, which is why it is worth turning off — but
 * batch-failure warnings elsewhere in the pipeline are NOT gated, since those
 * report style changes that were lost.
 */
const debugLog = (message: string): void => {
  if (__MSS_DEBUG_LOGGING__) console.log(message);
};

/**
 * Meta thunks often read counts off Office.js proxies (`items.length`), which
 * throw when the sync being measured is the thing that failed. Swallow that so
 * instrumentation never replaces the real error.
 */
const resolveMeta = (meta?: PerfMeta | (() => PerfMeta)): PerfMeta | undefined => {
  if (typeof meta !== "function") return meta;
  try {
    return meta();
  } catch {
    return undefined;
  }
};

const formatMeta = (meta?: PerfMeta): string =>
  meta
    ? ` {${Object.entries(meta)
        .map(([k, v]) => `${k}=${v}`)
        .join(", ")}}`
    : "";

/** Scale a millisecond duration to the largest unit that reads naturally. */
export function formatDuration(ms: number): string {
  const sign = ms < 0 ? "-" : "";
  const abs = Math.abs(ms);

  // Thresholds test the *rounded* value so 999.6ms doesn't render as "1000ms".
  if (Math.round(abs) < 1000) return `${sign}${Math.round(abs)}ms`;

  const totalSeconds = Math.round(abs / 1000);
  if (totalSeconds < 60) return `${sign}${(abs / 1000).toFixed(1)}s`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return hours > 0 ? `${sign}${hours}h ${minutes}m ${seconds}s` : `${sign}${minutes}m ${seconds}s`;
}

/** Elapsed-time gutter so a long run can be read as a timeline. */
const stamp = (elapsedMs: number): string => `+${formatDuration(elapsedMs)}`.padStart(10);

/**
 * Total wall-clock covered by at least one entry. Stage entries enclose the
 * syncs they contain (`stage:applyParagraphStyles` wraps every `applyPara:*`),
 * so a plain sum double-counts and would drive `unattributedMs` negative.
 */
function coveredMs(entries: PerfEntry[]): number {
  const intervals = entries
    .map((e) => ({ start: e.atMs, end: e.atMs + e.durationMs }))
    .sort((a, b) => a.start - b.start);

  let covered = 0;
  let cursor = -Infinity;
  for (const { start, end } of intervals) {
    if (end <= cursor) continue;
    covered += end - Math.max(start, cursor);
    cursor = end;
  }
  return covered;
}

/**
 * Time spent inside an entry but not inside any entry nested within it.
 *
 * Without this, an enclosing stage both dominates the ranking and hides its own
 * cost: a stage reading 8m with 25s of measured children has 7m35s of real work
 * nobody instrumented, and ranking by total duration makes that invisible.
 *
 * Nesting is decided by interval containment plus push order — `time()` records
 * in a `finally`, so a child is always pushed before its parent.
 */
export function selfMs(entries: PerfEntry[], index: number): number {
  const parent = entries[index];
  const parentEnd = parent.atMs + parent.durationMs;
  const nested = entries.filter(
    (e, i) => i < index && e.atMs >= parent.atMs && e.atMs + e.durationMs <= parentEnd
  );
  return parent.durationMs - coveredMs(nested);
}

export class PerfSession {
  private readonly startedAt = performance.now();
  private readonly entries: PerfEntry[] = [];

  constructor(private readonly label: string) {}

  /**
   * Time an async step, logging when it starts and when it finishes. `meta` may
   * be a thunk when its values are only known after the step completes (e.g. a
   * count loaded by the sync being measured).
   */
  async time<T>(
    name: string,
    fn: () => T | Promise<T>,
    meta?: PerfMeta | (() => PerfMeta)
  ): Promise<T> {
    const start = performance.now();
    debugLog(`[perf] ${stamp(start - this.startedAt)}  ▶ ${name}`);
    let failed = false;
    try {
      return await fn();
    } catch (error) {
      failed = true;
      throw error;
    } finally {
      const end = performance.now();
      const durationMs = end - start;
      const resolved = resolveMeta(meta);
      this.entries.push({
        name,
        durationMs,
        atMs: start - this.startedAt,
        meta: resolved,
      });
      debugLog(
        `[perf] ${stamp(end - this.startedAt)}  ${failed ? "✗" : "✓"} ${name} — ${formatDuration(durationMs)}${formatMeta(resolved)}`
      );
    }
  }

  /**
   * Log a point-in-time event on the run's timeline without recording an entry.
   * For progress that arrives *during* a step, e.g. backend chunk callbacks.
   */
  note(name: string, meta?: PerfMeta): void {
    debugLog(`[perf] ${stamp(performance.now() - this.startedAt)}  · ${name}${formatMeta(meta)}`);
  }

  /** Time a `context.sync()`. The primary measurement in this module. */
  timedSync(
    context: Word.RequestContext,
    name: string,
    meta?: PerfMeta | (() => PerfMeta)
  ): Promise<void> {
    return this.time(name, () => context.sync(), meta);
  }

  finish(): PerfReport {
    const totalMs = performance.now() - this.startedAt;
    const measured = coveredMs(this.entries);
    lastReport = {
      label: this.label,
      totalMs,
      unattributedMs: totalMs - measured,
      // `startedAt` is a performance.now() reading, which counts from the page's
      // timeOrigin rather than the epoch.
      startedAtEpochMs: performance.timeOrigin + this.startedAt,
      entries: this.entries,
    };
    if (__MSS_DEBUG_LOGGING__) {
      console.log(`[perf] ${formatPerfReport(lastReport)}`);
      console.table(
        this.entries.map((e, i) => ({
          step: e.name,
          self: formatDuration(selfMs(this.entries, i)),
          total: formatDuration(e.durationMs),
          ms: Math.round(e.durationMs),
          pct: totalMs > 0 ? `${((e.durationMs / totalMs) * 100).toFixed(1)}%` : "",
          ...e.meta,
        }))
      );
    }
    return lastReport;
  }
}

export function startRun(label: string): PerfSession {
  return new PerfSession(label);
}

/** Render a report as plain text for the copyable diagnostics block. */
export function formatPerfReport(report: PerfReport): string {
  const pct = (ms: number) =>
    report.totalMs > 0 ? `${((ms / report.totalMs) * 100).toFixed(1)}%` : "-";

  const col = (ms: number) => `${formatDuration(ms).padStart(9)}  ${pct(ms).padStart(6)}`;

  // Ranked by self time: an enclosing stage's total is mostly its children's
  // cost, so sorting on total buries whatever the stage itself is doing.
  const ranked = report.entries
    .map((e, i) => ({ entry: e, self: selfMs(report.entries, i) }))
    .sort((a, b) => b.self - a.self);

  const lines = [
    `${report.label} — total ${formatDuration(report.totalMs)}  (self / total)`,
    ...ranked.map(({ entry, self }) => {
      const total =
        Math.round(self) === Math.round(entry.durationMs)
          ? ""
          : ` [of ${formatDuration(entry.durationMs)}]`;
      return `  ${col(self)}  ${entry.name}${total}${formatMeta(entry.meta)}`;
    }),
    `  ${col(report.unattributedMs)}  (unattributed)`,
  ];
  return lines.join("\n");
}
